"use client";

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import Link from 'next/link';
import { FaSun, FaCloudRain, FaSnowflake, FaWind, FaInfoCircle, FaMapMarkerAlt, FaFilter, FaLeaf, FaRoute, FaMountain, FaCloud, FaSmog, FaTree, FaQuestionCircle, FaArrowRight, FaClock, FaMoon, FaLightbulb, FaCalendarAlt, FaMagic, FaSearch } from 'react-icons/fa';
import { weatherService, ENGELBERG_COORDINATES } from '@/services/WeatherService';
import BreadcrumbsWrapper from '@/components/BreadcrumbsWrapper';
import MapboxTouchFix from '@/components/TrailMapper/MapboxTouchFix';
import HeroContent from '@/components/HeroContent';
import { trailDiscoveryService } from '@/services/TrailDiscoveryService';
import { EnhancedTrailData, PointOfInterest, Season, CurrentWeather, WeatherCondition } from '@/types/enhancedTrail';
import WeatherWidget from '@/components/TrailDiscovery/WeatherWidget';
import TrailCarousel from '@/components/TrailDiscovery/TrailCarousel';
import TutorialOverlay from '@/components/TrailDiscovery/TutorialOverlay';
import TrailSearchModal from '@/components/TrailDiscovery/TrailSearchModal';
// External CSS needed for Mapbox
const MapboxCSS: React.FC = () => {
  useEffect(() => {
    // Dynamically load Mapbox CSS
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://api.mapbox.com/mapbox-gl-js/v3.1.0/mapbox-gl.css';
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, []);
  return null;
};
// Dynamically import the map component to avoid SSR issues
const EngelbergTrailMap = dynamic(
  () => import('@/components/TrailDiscovery/EngelbergTrailMap'),
  {
    ssr: false,
    loading: () => (
      <div className="flex flex-col items-center justify-center h-96 bg-gray-50 rounded-lg">
        <div className="text-4xl text-green-500 animate-pulse mb-4">
          <FaRoute />
        </div>
        <p className="text-lg text-gray-700">Loading Map...</p>
        <div className="w-40 h-1 mt-4 bg-gray-200 rounded-full overflow-hidden">
          <div className="h-full bg-green-500 animate-[loading_1.5s_ease-in-out_infinite]"></div>
        </div>
      </div>
    )
  }
);
// Define a type for the WeatherBasedRecommendations props
interface WeatherRecommendationsProps {
  trails: EnhancedTrailData[];
  weather: CurrentWeather;
  onTrailSelect: (trail: EnhancedTrailData, fromRecommended?: boolean) => void;
}
// Dynamically import the weather recommendations component
const WeatherBasedRecommendations = dynamic<WeatherRecommendationsProps>(
  () => import('@/components/TrailDiscovery/WeatherBasedRecommendations').then(mod => mod.default),
  {
    ssr: false,
    loading: () => (
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg shadow-sm mb-8 h-64 animate-pulse">
        <div className="h-8 w-48 bg-blue-200 rounded mb-4"></div>
        <div className="h-4 w-full bg-blue-100 rounded mb-2"></div>
        <div className="h-4 w-5/6 bg-blue-100 rounded mb-6"></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="h-32 bg-white rounded"></div>
          <div className="h-32 bg-white rounded"></div>
          <div className="h-32 bg-white rounded"></div>
        </div>
      </div>
    )
  }
);

// Helper to determine current season
const getCurrentSeason = (): Season => {
  const month = new Date().getMonth();
  if (month >= 2 && month <= 4) return 'spring';
  if (month >= 5 && month <= 7) return 'summer';
  if (month >= 8 && month <= 10) return 'autumn';
  return 'winter';
};

// Get seasonal title
const getSeasonTitle = (season: Season): string => {
  switch (season) {
    case 'spring': return 'Spring Adventures in Bloom';
    case 'summer': return 'Summer Alpine Explorations';
    case 'autumn': return 'Autumn Colors on the Trails';
    case 'winter': return 'Winter Wonderland Paths';
  }
};

// Get seasonal description
const getSeasonDescription = (season: Season): string => {
  switch (season) {
    case 'spring':
      return 'As the snow melts and flowers bloom, these trails offer spectacular spring experiences with waterfalls and alpine meadows coming to life.';
    case 'summer':
      return 'Perfect for long days of exploration, these summer trails provide incredible views and comfortable temperatures at higher elevations.';
    case 'autumn':
      return 'Experience the Alps transformed with vibrant fall colors. These trails offer peaceful walks through golden landscapes and clear mountain views.';
    case 'winter':
      return 'Discover the magic of snow-covered landscapes on these accessible winter trails. Remember to check conditions and bring appropriate equipment.';
  }
};

import { TrailSummary } from '@/services/OptimizedTrailService';
import { progressiveTrailService } from '@/services/ProgressiveTrailService';

interface TrailDiscoveryContentProps {
  lang: string;

}

export default function TrailDiscoveryContent({ lang }: TrailDiscoveryContentProps) {
  const { translate } = useLanguage();

  // State for trails, POIs, and UI elements
  const [trails, setTrails] = useState<EnhancedTrailData[]>([]);
  const [pois, setPOIs] = useState<PointOfInterest[]>([]);
  const [loading, setLoading] = useState<boolean>(true); // Only loading if no initial data
  const [error, setError] = useState<string | null>(null);
  const [trailSummary, setTrailSummary] = useState<TrailSummary | null>(null);
  const [isLoadingMoreTrails, setIsLoadingMoreTrails] = useState<boolean>(false);
  const [currentTrailBatch, setCurrentTrailBatch] = useState<number>(1);
  const [hasMoreTrails, setHasMoreTrails] = useState<boolean>(true);
  const TRAILS_PER_BATCH = 10; // Number of trails to load per batch

  // Weather, season, and time state
  const [weather, setWeather] = useState<CurrentWeather | null>(null);
  const [currentSeason, setCurrentSeason] = useState<Season>(getCurrentSeason());
  const [currentTime, setCurrentTime] = useState<Date>(new Date());
  const [smartRecommendationsActive, setSmartRecommendationsActive] = useState<boolean>(false);
  const [remainingDaylight, setRemainingDaylight] = useState<number>(0); // In hours

  // UI interaction state
  const [isMapReady, setIsMapReady] = useState<boolean>(false);
  const [selectedTrail, setSelectedTrail] = useState<EnhancedTrailData | null>(null);
  const [selectedPOI, setSelectedPOI] = useState<PointOfInterest | null>(null);
  const [showAllTrails, setShowAllTrails] = useState<boolean>(true);
  const [showTutorial, setShowTutorial] = useState<boolean>(false);
  const [showSearchModal, setShowSearchModal] = useState<boolean>(false);
  const [scrollToNavigationOnSelect, setScrollToNavigationOnSelect] = useState<boolean>(false);

  // Refs for scrolling to different sections
  const detailsRef = useRef<HTMLDivElement>(null);
  const navigationButtonsRef = useRef<HTMLDivElement>(null);

  // Filtering state
  const [activeFilters, setActiveFilters] = useState({
    difficulty: null as string | null,
    minDistance: 0,
    maxDistance: 20,
    season: null as Season | null,
    surfaceType: null as string | null,
    familySuitability: false,
  });

  // Update current time and calculate remaining daylight every minute
  useEffect(() => {
    const calculateDaylight = () => {
      const now = new Date();
      setCurrentTime(now);

      // Simplified sunset time calculation based on season and location (Engelberg)
      const month = now.getMonth();
      let sunsetHour = 0;

      // Approximate sunset times for Engelberg by season
      if (month >= 2 && month <= 4) { // Spring
        sunsetHour = 19 + (month - 2); // 19:00 to 21:00
      } else if (month >= 5 && month <= 7) { // Summer
        sunsetHour = 21; // 21:00
      } else if (month >= 8 && month <= 10) { // Autumn
        sunsetHour = 21 - (month - 7); // 21:00 to 18:00
      } else { // Winter
        sunsetHour = 17; // 17:00
      }

      const currentHour = now.getHours() + (now.getMinutes() / 60);
      const remaining = Math.max(0, sunsetHour - currentHour);
      setRemainingDaylight(remaining);
    };

    calculateDaylight(); // Calculate immediately

    const timer = setInterval(() => {
      calculateDaylight();
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  // Helper function to check if a trail is suitable for current time of day
  const isTrailSuitableForCurrentTime = (trail: EnhancedTrailData): boolean => {
    if (!trail.weatherConsiderations) return true;

    // Check if enough daylight remains to complete the trail
    if (trail.estimatedDuration && remainingDaylight < trail.estimatedDuration + 1) {
      return false; // Not enough daylight (adding 1 hour safety buffer)
    }

    // Check best time of day preference
    if (trail.weatherConsiderations.bestTimeOfDay) {
      const hour = currentTime.getHours();
      const isMorning = hour >= 5 && hour < 12;
      const isAfternoon = hour >= 12 && hour < 17;
      const isEvening = hour >= 17 && hour < 21;

      if (trail.weatherConsiderations.bestTimeOfDay === 'morning' && !isMorning) return false;
      if (trail.weatherConsiderations.bestTimeOfDay === 'afternoon' && !isAfternoon) return false;
      if (trail.weatherConsiderations.bestTimeOfDay === 'evening' && !isEvening) return false;
    }

    return true;
  };

  // Fetch trail data progressively
  useEffect(() => {


    // No initial data, need to fetch everything
    const fetchData = async () => {
      try {
        setLoading(true);

        // First fetch weather to use in trail recommendations
        let currentWeather;
        try {
          currentWeather = await weatherService.getCurrentWeather();
          setWeather(currentWeather);
        } catch (weatherErr) {
          console.error('Error fetching weather:', weatherErr);
          try {
            currentWeather = await weatherService.getCurrentWeather(ENGELBERG_COORDINATES);
            setWeather(currentWeather);
          } catch (fallbackErr) {
            console.error('Error fetching fallback weather:', fallbackErr);
          }
        }

        // Use a single update approach rather than callbacks
        if (currentWeather) {
          // Fetch summary first for immediate display
          const summary = await progressiveTrailService.getSummaryData();

          // Update summary without changing loading state
          if (summary) {
            setTrailSummary(summary);
          }

          // Then fetch initial recommendations and POIs in parallel
          const [initialRecommendations, pois] = await Promise.all([
            progressiveTrailService.getInitialRecommendations(currentWeather, currentSeason),
            trailDiscoveryService.getPointsOfInterest()
          ]);

          // Update with batch of data at once
          if (initialRecommendations) {
            setTrails(initialRecommendations);
          }

          if (pois) {
            setPOIs(pois);
          }

          // Mark loading complete after core data is loaded
          setLoading(false);

          // We'll load remaining trails progressively as user scrolls
          // Just loading the first batch initially to improve performance
          const firstBatch = await loadTrailBatch(1, TRAILS_PER_BATCH);
          if (firstBatch && firstBatch.length > 0) {
            setTrails(prevTrails => {
              const existingIds = new Set((prevTrails || []).map(t => t.id));
              const newTrails = firstBatch.filter(t => !existingIds.has(t.id));
              return [...(prevTrails || []), ...newTrails];
            });

            // If we received fewer trails than the batch size, there are no more trails to load
            if (firstBatch.length < TRAILS_PER_BATCH) {
              setHasMoreTrails(false);
            }
          }
        } else {
          // Fallback to traditional loading if weather is not available
          const [fetchedTrails, fetchedPOIs] = await Promise.all([
            trailDiscoveryService.getEnhancedTrails(),
            trailDiscoveryService.getPointsOfInterest()
          ]);

          setTrails(fetchedTrails);
          setPOIs(fetchedPOIs);
          setLoading(false);
        }

        setError(null);
      } catch (err) {
        console.error('Error fetching trail data:', err);
        setError('Failed to load trail data. Please try again later.');
        setLoading(false);
      }
    };

    fetchData();
  }, [currentSeason]);

  // Initialize weather data
  useEffect(() => {
    const fetchWeather = async () => {
      try {
        const currentWeather = await weatherService.getCurrentWeather();
        setWeather(currentWeather);
      } catch (error) {
        console.error('Error fetching weather data:', error);
        // Fall back to Engelberg default coordinates if there's an error
        const engelbergWeather = await weatherService.getCurrentWeather(ENGELBERG_COORDINATES);
        setWeather(engelbergWeather);
      }
    };

    fetchWeather();

    // Refresh weather periodically
    const interval = setInterval(() => {
      fetchWeather();
    }, 60000 * 30); // Update every 30 minutes

    return () => clearInterval(interval);
  }, []);

  // Set timeout to ensure Mapbox CSS is fully loaded
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const timer = setTimeout(() => {
        setIsMapReady(true);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, []);

  /**
   * Helper function to load a batch of trails
   */
  const loadTrailBatch = async (batchNumber: number, batchSize: number): Promise<EnhancedTrailData[]> => {
    try {
      const allTrails = await progressiveTrailService.getAllTrails();

      if (!allTrails || allTrails.length === 0) {
        return [];
      }

      // Calculate start and end indices for the batch
      const startIndex = (batchNumber - 1) * batchSize;
      const endIndex = startIndex + batchSize;

      // Return the requested batch
      return allTrails.slice(startIndex, endIndex);
    } catch (err) {
      console.error('Error loading trail batch:', err);
      return [];
    }
  };

  /**
   * Load more trails when the user approaches the end of the carousel
   */
  const loadMoreTrails = async () => {
    if (isLoadingMoreTrails || !hasMoreTrails) return;

    try {
      setIsLoadingMoreTrails(true);
      const nextBatch = currentTrailBatch + 1;

      const newTrails = await loadTrailBatch(nextBatch, TRAILS_PER_BATCH);

      if (newTrails && newTrails.length > 0) {
        setTrails(prevTrails => {
          const existingIds = new Set(prevTrails.map(t => t.id));
          const uniqueNewTrails = newTrails.filter(t => !existingIds.has(t.id));
          return [...prevTrails, ...uniqueNewTrails];
        });

        setCurrentTrailBatch(nextBatch);

        // If we received fewer trails than the batch size, there are no more trails to load
        if (newTrails.length < TRAILS_PER_BATCH) {
          setHasMoreTrails(false);
        }
      } else {
        setHasMoreTrails(false);
      }
    } catch (err) {
      console.error('Error loading more trails:', err);
    } finally {
      setIsLoadingMoreTrails(false);
    }
  };

  // Filter trails based on active filters
  const filteredTrails = useMemo(() => {
    if (!activeFilters || !trails || trails.length === 0) return trails || [];

    return trails.filter(trail => {
      // Filter by difficulty
      if (activeFilters.difficulty && trail.difficulty !== activeFilters.difficulty) {
        return false;
      }

      // Filter by distance
      if ((trail.totalDistance ?? 0) < activeFilters.minDistance ||
        (trail.totalDistance ?? 0) > activeFilters.maxDistance) {
        return false;
      }

      // Filter by season
      if (activeFilters.season &&
        trail.seasonalInfo?.bestSeasons &&
        !trail.seasonalInfo.bestSeasons.includes(activeFilters.season)) {
        return false;
      }

      // Filter by surface type
      if (activeFilters.surfaceType &&
        trail.surfaceType !== activeFilters.surfaceType) {
        return false;
      }

      // Filter by family suitability
      if (activeFilters.familySuitability &&
        trail.familySuitability &&
        trail.familySuitability < 7) {
        return false;
      }

      return true;
    });
  }, [activeFilters, trails]);

  // Get weather-appropriate trails
  const weatherAppropriateTrails = useMemo(() => {
    if (!weather || !trails || trails.length === 0) return [];

    return trails.filter(trail => {
      if (!trail.weatherConsiderations) return true;

      // Check temperature range
      if (trail.weatherConsiderations.minTemperature !== undefined &&
        weather.temperature < trail.weatherConsiderations.minTemperature) {
        return false;
      }

      if (trail.weatherConsiderations.maxTemperature !== undefined &&
        weather.temperature > trail.weatherConsiderations.maxTemperature) {
        return false;
      }

      // Check rain conditions
      if (trail.weatherConsiderations.avoidInRain && weather.isRaining) {
        return false;
      }

      // Check snow conditions
      if (trail.weatherConsiderations.avoidInSnow && weather.isSnowing) {
        return false;
      }

      // Check wind sensitivity
      if (trail.weatherConsiderations.windSensitive && weather.windSpeed > 20) {
        return false;
      }

      // Check suitable weather conditions
      if (trail.weatherConsiderations.suitableWeather &&
        !trail.weatherConsiderations.suitableWeather.includes(weather.condition)) {
        return false;
      }

      return true;
    });
  }, [weather, trails]);

  // Get smart recommended trails based on time, weather, and season
  const smartRecommendedTrails = useMemo(() => {
    if (!trails || trails.length === 0 || !weather) return [];

    return trails.filter(trail => {
      // Must be suitable for current weather
      if (!weatherAppropriateTrails.includes(trail)) return false;

      // Must be suitable for current season
      if (trail.seasonalInfo?.bestSeasons && !trail.seasonalInfo.bestSeasons.includes(currentSeason)) {
        return false;
      }

      // Must be suitable for current time of day
      if (!isTrailSuitableForCurrentTime(trail)) return false;

      return true;
    }).sort((a, b) => {
      // Sort by score (prioritize trails with seasonal notes for current season)
      const aHasSeasonalNote = a.seasonalNotes && a.seasonalNotes[currentSeason] ? 1 : 0;
      const bHasSeasonalNote = b.seasonalNotes && b.seasonalNotes[currentSeason] ? 1 : 0;

      // Consider difficulty based on remaining daylight
      const aIsDifficult = a.difficulty === 'difficult' || a.difficulty === 'extreme';
      const bIsDifficult = b.difficulty === 'difficult' || b.difficulty === 'extreme';

      // When daylight is limited, prioritize easier trails
      if (remainingDaylight < 4) {
        if (aIsDifficult && !bIsDifficult) return 1;
        if (!aIsDifficult && bIsDifficult) return -1;
      }

      return bHasSeasonalNote - aHasSeasonalNote;
    });
  }, [trails, weather, currentSeason, currentTime, remainingDaylight, weatherAppropriateTrails]);

  // Get seasonal trails

  const seasonalTrails = useMemo(() => {
    if (!trails || trails.length === 0) return [];

    return trails.filter(trail =>
      trail.seasonalInfo?.bestSeasons?.includes(currentSeason)
    );
  }, [currentSeason]);
  // Enhanced auto-scroll functionality - scrolls directly to navigation buttons when a trail is selected
  useEffect(() => {
    if (selectedTrail && scrollToNavigationOnSelect) {
      // Use a small timeout to ensure the details section is fully rendered
      setTimeout(() => {
        // Target the navigation buttons directly for more precise scrolling
        if (navigationButtonsRef.current) {
          // On mobile, use a smooth scroll with offset to ensure buttons are visible
          const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

          if (isMobile) {
            // Calculate appropriate offset to ensure buttons are visible
            const offset = window.innerHeight * 0.15; // Small offset from top
            const buttonPosition = navigationButtonsRef.current.getBoundingClientRect().top;
            const scrollPosition = window.pageYOffset + buttonPosition - offset;

            // Scroll with smooth behavior
            window.scrollTo({
              top: scrollPosition,
              behavior: 'smooth'
            });
          } else {
            // On desktop, simply scroll into view
            navigationButtonsRef.current.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
          }
        }
        // Reset the flag after scrolling
        setScrollToNavigationOnSelect(false);
      }, 400); // Slightly longer timeout to ensure rendering is complete
    }
  }, [selectedTrail, scrollToNavigationOnSelect]);

  // Ensure trail data is loaded for map display
  const ensureTrailDataForMap = async (trailId: string) => {
    try {
      // Check if the trail exists and has waypoints data
      const existingTrail = trails.find(t => t.id === trailId);

      if (!existingTrail) return;

      // If the trail already has waypoints, no need to fetch again
      if (existingTrail.waypoints && existingTrail.waypoints.length > 0) return;

      console.log(`Fetching complete trail data for ${trailId}`);
      // Fetch the complete trail data with waypoints
      const completeTrail = await trailDiscoveryService.getEnhancedTrail(trailId);

      if (completeTrail && completeTrail.waypoints) {
        console.log(`Received waypoints for ${trailId}: ${completeTrail.waypoints.length} points`);
        // Update our local trail state with the complete data
        setTrails(prevTrails =>
          prevTrails.map(trail =>
            trail.id === trailId ? { ...trail, waypoints: completeTrail.waypoints } : trail
          )
        );
      }
    } catch (err) {
      console.error(`Error loading trail waypoints for map: ${trailId}`, err);
    }
  };

  // Enhanced trail selection handler with source tracking
  const handleTrailSelect = (trail: EnhancedTrailData, fromRecommended: boolean = false) => {
    setSelectedTrail(trail);
    setSelectedPOI(null);
    // When a specific trail is selected, we don't want to show all trails
    setShowAllTrails(false);

    // Ensure the selected trail has waypoint data for map display
    ensureTrailDataForMap(trail.id);

    // Set the flag to scroll to navigation buttons if selection is from recommended trails
    if (fromRecommended) {
      setScrollToNavigationOnSelect(true);
    }
  };

  // Handle trail carousel navigation events to trigger loading more trails
  const handleCarouselNavigation = (index: number, total: number) => {
    // If user is approaching the end of loaded trails, load more
    if (hasMoreTrails && !isLoadingMoreTrails && index >= total - 3) {
      loadMoreTrails();
    }

    // Ensure the current trail has waypoint data for map display
    if (filteredTrails && filteredTrails[index]) {
      ensureTrailDataForMap(filteredTrails[index].id);

      // Preload waypoints for nearby trails to ensure smooth carousel experience
      // Preload the next 2 trails
      for (let i = 1; i <= 2; i++) {
        const nextIndex = (index + i) % total;
        if (filteredTrails[nextIndex]) {
          ensureTrailDataForMap(filteredTrails[nextIndex].id);
        }
      }

      // Preload the previous trail
      const prevIndex = (index - 1 + total) % total;
      if (filteredTrails[prevIndex]) {
        ensureTrailDataForMap(filteredTrails[prevIndex].id);
      }
    }
  };

  // Toggle between showing all trails or just the selected one
  const toggleShowAllTrails = () => {
    setShowAllTrails(!showAllTrails);
    // If switching to show all trails, clear the selected trail
    if (!showAllTrails) {
      setSelectedTrail(null);
    }
  };

  // Handle POI selection

  const handlePOISelect = (poi: PointOfInterest) => {
    setSelectedPOI(poi);

    // Find trails that connect to this POI
    const connectedTrails = (trails || []).filter(trail =>
      trail.pointsOfInterest?.some(relation => relation.poiId === poi.id)
    );
    // If only one trail is connected, select it
    if (connectedTrails.length === 1) {
      setSelectedTrail(connectedTrails[0]);
    } else {
      setSelectedTrail(null);
    }
  };
  // Handle filter changes
  const handleFilterChange = (filter: Partial<typeof activeFilters>) => {
    setActiveFilters(prev => ({
      ...prev,
      ...filter
    }));
  };
  // Loading state render
  // Replace the current loading state render with this improved version
  if (loading) {
    return (
      <main className="min-h-screen fade-in no-flicker">
        <MapboxCSS />
        <MapboxTouchFix />
        <BreadcrumbsWrapper />

        <div className="container mx-auto px-4 py-8">
          {/* Skeleton for weather widget */}
          <div className="absolute top-4 right-4 z-10 bg-white bg-opacity-90 p-3 rounded-lg shadow-md h-[68px] w-[120px]">
            <div className="flex items-center">
              <div className="w-8 h-8 rounded-full bg-gray-200 animate-pulse mr-2"></div>
              <div>
                <div className="h-4 w-12 bg-gray-200 rounded animate-pulse mb-1"></div>
                <div className="h-3 w-16 bg-gray-100 rounded animate-pulse"></div>
              </div>
            </div>
          </div>

          {/* Skeleton for main content */}
          <div className="mb-8">
            <div className="h-8 w-64 bg-gray-200 rounded animate-pulse mb-4"></div>
            <div className="h-4 w-full bg-gray-100 rounded animate-pulse mb-2"></div>
            <div className="h-4 w-5/6 bg-gray-100 rounded animate-pulse"></div>
          </div>

          {/* Skeleton for recommendations */}
          <div className="mb-8">
            <div className="h-7 w-48 bg-gray-200 rounded animate-pulse mb-4"></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-white rounded-lg shadow-sm h-48 p-4">
                  <div className="h-5 w-32 bg-gray-200 rounded animate-pulse mb-3"></div>
                  <div className="h-3 w-24 bg-gray-100 rounded animate-pulse mb-2"></div>
                  <div className="h-20 bg-gray-50 rounded animate-pulse mb-3"></div>
                  <div className="h-6 bg-blue-100 rounded animate-pulse"></div>
                </div>
              ))}
            </div>
          </div>

          {/* Skeleton for map area */}
          <div className="h-[600px] bg-gray-50 rounded-lg animate-pulse flex items-center justify-center">
            <div className="text-center">
              <div className="text-4xl text-green-500 animate-pulse mb-4">
                <FaRoute />
              </div>
              <p className="text-lg text-gray-700">Loading Map Data...</p>
            </div>
          </div>
        </div>
      </main>
    );
  }

  // Error state render
  if (error) {
    return (
      <main className="min-h-screen">
        <MapboxCSS />
        <MapboxTouchFix />

        {/* <HeroContent
          sectionId="trail-discovery"
          fallbackTitle={translate('trailDiscovery.title', 'Engelberg Trail & POI Discovery Hub')}
          fallbackSubtitle={translate('trailDiscovery.subtitle', 'Explore trails and points of interest around Engelberg with our interactive map')}
          fallbackImage="/images/attractions/engelberg-landscapes/cover.webp"
        /> */}
        <BreadcrumbsWrapper />

        <div className="container mx-auto px-4 py-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 my-8">
            <h2 className="text-xl font-semibold text-red-700 mb-2">{translate('trailDiscovery.error', 'Error Loading Data')}</h2>
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
            >
              {translate('trailDiscovery.retry', 'Retry Loading')}
            </button>
          </div>
        </div>
      </main>
    );
  }
  // Render weather icon based on condition
  const renderWeatherIcon = (condition: WeatherCondition) => {
    switch (condition) {
      case 'sunny': return <FaSun className="text-yellow-500" />;
      case 'cloudy': return <FaCloud className="text-gray-400" />;
      case 'rainy': return <FaCloudRain className="text-blue-400" />;
      case 'snowy': return <FaSnowflake className="text-blue-200" />;
      case 'foggy': return <FaSmog className="text-gray-300" />;
      case 'windy': return <FaWind className="text-blue-300" />;
      case 'light_rain': return <FaCloudRain className="text-blue-300" />;
      case 'clear': return <FaSun className="text-yellow-400" />;
      default: return <FaSun className="text-yellow-500" />;
    }
  };

  return (
    <main className="min-h-screen">
      <MapboxCSS />
      <MapboxTouchFix />

      {/* <HeroContent
        sectionId="trail-discovery"
        fallbackTitle={translate('trailDiscovery.title', 'Engelberg Trail & POI Discovery Hub')}
        fallbackSubtitle={translate('trailDiscovery.subtitle', 'Explore trails and points of interest around Engelberg with our interactive map')}
        fallbackImage="/images/attractions/engelberg-landscapes/cover.webp"
      /> */}
      <BreadcrumbsWrapper />

      <div className="container mx-auto px-4 py-8">
        {/* Introduction */}
        <div className="mb-8 relative">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-3xl font-bold">
              {translate('trailDiscovery.heading', 'Discover Engelberg\'s Scenic Trails')}
            </h2>
            <div className="flex gap-2">
              <button
                onClick={() => setShowSearchModal(true)}
                className="flex items-center px-3 py-2 bg-green-100 text-green-700 rounded-full hover:bg-green-200 transition-colors"
              >
                <FaSearch className="mr-2" />
                {translate('trailDiscovery.searchTrails', 'Search Trails')}
              </button>
              <button
                onClick={() => setShowTutorial(true)}
                className="flex items-center px-3 py-2 bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors"
              >
                <FaQuestionCircle className="mr-2" />
                {translate('trailDiscovery.howItWorks', 'How it works')}
              </button>
            </div>
          </div>
          <p className="text-lg mb-6">
            {translate('trailDiscovery.description', 'Explore the beautiful hiking trails around Engelberg with our interactive map. Find trails based on weather conditions, difficulty level, and seasonal recommendations. Discover points of interest along the way and plan your perfect outdoor adventure during your stay at Tannenboden Chalet.')}
          </p>
        </div>

        {/* Smart Trail Recommendations */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-semibold">
              {smartRecommendationsActive
                ? translate('trailDiscovery.perfectForNow', 'Perfect Trails for Now')
                : translate('trailDiscovery.weatherBasedRecommendations', 'Weather-Based Recommendations')}
            </h2>

            <button
              onClick={() => setSmartRecommendationsActive(!smartRecommendationsActive)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${smartRecommendationsActive
                ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg scale-105'
                : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                }`}
            >
              {smartRecommendationsActive ? (
                <>
                  <FaMagic className="text-yellow-300" />
                  <span>Smart Recommendations</span>
                  <span className="flex items-center ml-2 px-2 py-1 bg-white bg-opacity-20 rounded-full text-xs">
                    <FaLightbulb className="mr-1 text-yellow-300" />
                    {remainingDaylight.toFixed(1)}h
                  </span>
                </>
              ) : (
                <>
                  <FaClock />
                  <span>Perfect for Now</span>
                </>
              )}
            </button>
          </div>

          {smartRecommendationsActive && remainingDaylight < 3 && (
            <div className="bg-amber-50 border-l-4 border-amber-500 p-3 rounded mb-4">
              <div className="flex items-center">
                <FaSun className="text-amber-500 mr-2" />
                <p className="text-amber-700">
                  {translate('trailDiscovery.limitedDaylight',
                    `Limited daylight remaining (${remainingDaylight.toFixed(1)} hours). Only showing trails you can complete safely before sunset.`
                  )}
                </p>
              </div>
            </div>
          )}

          {weather && (
            <div className="mb-8">
              {smartRecommendationsActive ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {smartRecommendedTrails.length > 0 ? (
                    smartRecommendedTrails.slice(0, 6).map(trail => (
                      <div
                        key={trail.id}
                        onClick={() => handleTrailSelect(trail, true)}
                        className="bg-white bg-opacity-95 rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-all cursor-pointer border border-blue-100"
                      >
                        <div className="p-4">
                          <div className="flex justify-between items-start mb-2">
                            <h3 className="font-bold text-lg">{trail.name}</h3>
                            <div className="flex space-x-1">
                              {trail.seasonalInfo?.bestSeasons?.includes(currentSeason) && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-green-100 text-green-800">
                                  <FaCalendarAlt className="mr-1" />
                                  {currentSeason}
                                </span>
                              )}
                              {trail.weatherConsiderations?.bestTimeOfDay && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800">
                                  <FaClock className="mr-1" />
                                  {trail.weatherConsiderations.bestTimeOfDay}
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center text-sm text-gray-600 mb-3">
                            <span className="mr-3">{trail.totalDistance} km</span>
                            <span className="mr-3 flex items-center">
                              <FaClock className="mr-1 text-xs" />
                              {trail.estimatedDuration} h
                            </span>
                            <span className="px-2 py-0.5 bg-gray-100 rounded text-xs capitalize">{trail.difficulty}</span>
                          </div>

                          {/* Daylight indicator */}
                          {trail.estimatedDuration && (
                            <div className="mb-3">
                              <div className="flex justify-between items-center text-xs mb-1">
                                <span>Time to complete</span>
                                <span className={remainingDaylight < trail.estimatedDuration + 1 ? 'text-red-600 font-bold' : 'text-green-600'}>
                                  {trail.estimatedDuration}h / {remainingDaylight.toFixed(1)}h daylight
                                </span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-1.5">
                                <div
                                  className={`h-1.5 rounded-full ${remainingDaylight < trail.estimatedDuration + 1
                                    ? 'bg-red-500'
                                    : remainingDaylight < trail.estimatedDuration + 3
                                      ? 'bg-yellow-500'
                                      : 'bg-green-500'
                                    }`}
                                  style={{ width: `${Math.min(100, (trail.estimatedDuration / remainingDaylight) * 100)}%` }}
                                ></div>
                              </div>
                            </div>
                          )}

                          {trail.seasonalNotes && trail.seasonalNotes[currentSeason] && (
                            <div className="text-sm bg-blue-50 p-2 rounded border-l-2 border-blue-300 mb-3">
                              <p className="italic text-gray-700">{trail.seasonalNotes[currentSeason]}</p>
                            </div>
                          )}

                          <button className="mt-2 w-full py-1.5 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors text-sm">
                            {translate('trailDiscovery.viewTrail', 'View Trail')}
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="col-span-full bg-gray-50 rounded-lg p-6 text-center">
                      <FaMoon className="mx-auto text-2xl text-gray-400 mb-2" />
                      <h3 className="text-lg font-medium text-gray-700 mb-1">
                        {translate('trailDiscovery.noSuitableTrails', 'No suitable trails for current conditions')}
                      </h3>
                      <p className="text-gray-500">
                        {translate('trailDiscovery.tryDifferentFilters', 'Try different filters or come back during daylight hours')}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <WeatherBasedRecommendations
                  trails={weatherAppropriateTrails}
                  weather={weather}
                  onTrailSelect={(trail) => handleTrailSelect(trail, true)}
                />
              )}
            </div>
          )}
        </div>

        {/* Interactive Map Section */}
        <div className="mb-12">
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Map Filters Sidebar */}
            <div className="lg:w-1/4">
              <div className="bg-white rounded-lg shadow-md p-4 sticky top-24">
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <FaFilter className="mr-2 text-blue-500" />
                  {translate('trailDiscovery.filterTrails', 'Filter Trails')}
                </h3>

                {/* Difficulty Filter */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {translate('trailDiscovery.difficulty', 'Difficulty')}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {['easy', 'moderate', 'difficult', 'extreme'].map(difficulty => (
                      <button
                        key={difficulty}
                        className={`px-3 py-1 text-sm rounded-full ${activeFilters.difficulty === difficulty
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                          }`}
                        onClick={() => handleFilterChange({
                          difficulty: activeFilters.difficulty === difficulty ? null : difficulty
                        })}
                      >
                        {translate(`trailDiscovery.${difficulty}`, difficulty)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Distance Filter */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {translate('trailDiscovery.distance', 'Distance (km)')}
                  </label>
                  <div className="px-2">
                    <input
                      type="range"
                      min="0"
                      max="20"
                      step="1"
                      value={activeFilters.maxDistance}
                      onChange={(e) => handleFilterChange({ maxDistance: parseInt(e.target.value) })}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>0</span>
                      <span>5</span>
                      <span>10</span>
                      <span>15</span>
                      <span>20+</span>
                    </div>
                    <p className="text-center mt-2 text-sm">
                      {`${activeFilters.minDistance} - ${activeFilters.maxDistance} km`}
                    </p>
                  </div>
                </div>

                {/* Season Filter */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {translate('trailDiscovery.season', 'Best Season')}
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {(['spring', 'summer', 'autumn', 'winter'] as Season[]).map(season => (
                      <button
                        key={season}
                        className={`px-3 py-2 text-sm rounded-md flex items-center justify-center ${activeFilters.season === season
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                          }`}
                        onClick={() => handleFilterChange({
                          season: activeFilters.season === season ? null : season
                        })}
                      >
                        {season === 'spring' && <FaLeaf className="mr-1" />}
                        {season === 'summer' && <FaSun className="mr-1" />}
                        {season === 'autumn' && <FaTree className="mr-1" />}
                        {season === 'winter' && <FaSnowflake className="mr-1" />}
                        {translate(`trailDiscovery.${season}`, season)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Surface Type Filter */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {translate('trailDiscovery.surfaceType', 'Surface Type')}
                  </label>
                  <select
                    className="w-full p-2 border border-gray-300 rounded-md"
                    value={activeFilters.surfaceType || ''}
                    onChange={(e) => handleFilterChange({
                      surfaceType: e.target.value === '' ? null : e.target.value
                    })}
                  >
                    <option value="">
                      {translate('trailDiscovery.allSurfaces', 'All Surfaces')}
                    </option>
                    <option value="paved">
                      {translate('trailDiscovery.paved', 'Paved')}
                    </option>
                    <option value="gravel">
                      {translate('trailDiscovery.gravel', 'Gravel')}
                    </option>
                    <option value="dirt">
                      {translate('trailDiscovery.dirt', 'Dirt')}
                    </option>
                    <option value="rocky">
                      {translate('trailDiscovery.rocky', 'Rocky')}
                    </option>
                    <option value="mixed">
                      {translate('trailDiscovery.mixed', 'Mixed')}
                    </option>
                  </select>
                </div>

                {/* Family-Friendly Toggle */}
                <div className="mb-4">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={activeFilters.familySuitability}
                      onChange={(e) => handleFilterChange({
                        familySuitability: e.target.checked
                      })}
                      className="h-4 w-4 text-blue-500 rounded"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      {translate('trailDiscovery.familyFriendly', 'Family-Friendly Only')}
                    </span>
                  </label>
                </div>

                {/* Reset Filters Button */}
                <button
                  className="w-full mt-2 px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                  onClick={() => setActiveFilters({
                    difficulty: null,
                    minDistance: 0,
                    maxDistance: 20,
                    season: null,
                    surfaceType: null,
                    familySuitability: false
                  })}
                >
                  {translate('trailDiscovery.resetFilters', 'Reset Filters')}
                </button>
              </div>
            </div>

            {/* Main Map Area */}
            <div className="lg:w-3/4">
              <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="h-[600px] relative">
                  {isMapReady && (
                    <>
                      <EngelbergTrailMap
                        trails={filteredTrails || []}
                        pois={pois}
                        weatherConditions={weather || undefined}
                        onTrailSelect={(trail) => handleTrailSelect(trail, false)}
                        onPoiSelect={handlePOISelect}
                        activeFilters={activeFilters}
                        selectedTrail={selectedTrail}
                        selectedPOI={selectedPOI}
                        showAll={showAllTrails}
                      />

                      {/* Trail Carousel for navigating between trails */}
                      <TrailCarousel
                        trails={filteredTrails || []}
                        onTrailSelect={(trail) => handleTrailSelect(trail, false)}
                        selectedTrail={selectedTrail}
                        onShowAllToggle={toggleShowAllTrails}
                        showAll={showAllTrails}
                        onNavigate={handleCarouselNavigation}
                        isLoadingMore={isLoadingMoreTrails}
                      />
                    </>
                  )}

                  {/* Weather Widget */}
                  {weather && (
                    <WeatherWidget weather={weather} />
                  )}
                </div>

                {/* Selected Entity Details - added ref for auto-scrolling */}
                {(selectedTrail || selectedPOI) && (
                  <div ref={detailsRef} className="p-4 border-t border-gray-200">
                    {selectedTrail && (
                      <div className="mb-4">
                        <h3 className="text-xl font-bold mb-2">{selectedTrail.name}</h3>
                        <div className="flex flex-wrap gap-2 mb-3">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {selectedTrail.totalDistance} km
                          </span>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {selectedTrail.estimatedDuration} hrs
                          </span>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 capitalize">
                            {selectedTrail.difficulty}
                          </span>
                          {selectedTrail.tags?.map(tag => (
                            <span key={tag} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              {tag}
                            </span>
                          ))}
                        </div>
                        <p className="text-gray-700 mb-4">{selectedTrail.description}</p>

                        {/* Seasonal notes - collapsible section */}
                        {selectedTrail.seasonalNotes && Object.keys(selectedTrail.seasonalNotes).length > 0 && (
                          <div className="mb-4 bg-blue-50 bg-opacity-70 rounded-lg p-3">
                            <div
                              className="flex items-center justify-between cursor-pointer"
                              onClick={() => {
                                const elem = document.getElementById(`seasonal-notes-${selectedTrail.id}`);
                                if (elem) {
                                  elem.classList.toggle('hidden');
                                }
                              }}
                            >
                              <h4 className="font-semibold text-blue-800">
                                {translate('trailDiscovery.seasonalNotes', 'Seasonal Notes')}
                              </h4>
                              <FaArrowRight className="text-blue-500 transform transition-transform duration-300" />
                            </div>

                            <div id={`seasonal-notes-${selectedTrail.id}`} className="pt-2 hidden">
                              {(['spring', 'summer', 'autumn', 'winter'] as Season[]).map(season => (
                                selectedTrail.seasonalNotes?.[season] ? (
                                  <div
                                    key={season}
                                    className={`mb-2 p-2 rounded ${season === currentSeason ? 'bg-blue-100 border-l-4 border-blue-500' : ''
                                      }`}
                                  >
                                    <div className="flex items-center mb-1">
                                      {season === 'spring' && <FaLeaf className="text-green-500 mr-2" />}
                                      {season === 'summer' && <FaSun className="text-yellow-500 mr-2" />}
                                      {season === 'autumn' && <FaTree className="text-orange-500 mr-2" />}
                                      {season === 'winter' && <FaSnowflake className="text-blue-300 mr-2" />}
                                      <span className="font-medium capitalize">{season}</span>
                                      {season === currentSeason && (
                                        <span className="ml-2 text-xs bg-blue-200 px-1.5 py-0.5 rounded-full text-blue-800">
                                          Current Season
                                        </span>
                                      )}
                                    </div>
                                    <p className="text-sm text-gray-700 pl-6">{selectedTrail.seasonalNotes[season]}</p>
                                  </div>
                                ) : null
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Weather and seasonal information */}
                        {selectedTrail.weatherConsiderations && (
                          <div className="mb-4">
                            <h4 className="font-semibold text-sm text-gray-700 mb-1">
                              {translate('trailDiscovery.weatherConsiderations', 'Weather Considerations')}
                            </h4>
                            {selectedTrail.weatherConsiderations.weatherCautions && (
                              <p className="text-sm text-red-600 mb-2">
                                <FaInfoCircle className="inline mr-1" />
                                {selectedTrail.weatherConsiderations.weatherCautions}
                              </p>
                            )}
                            <div className="text-sm text-gray-600">
                              {selectedTrail.weatherConsiderations.bestTimeOfDay && (
                                <span className="mr-4">
                                  Best time: <span className="font-medium capitalize">{selectedTrail.weatherConsiderations.bestTimeOfDay}</span>
                                </span>
                              )}
                              {selectedTrail.weatherConsiderations.minTemperature !== undefined && (
                                <span className="mr-4">
                                  Min temp: <span className="font-medium">{selectedTrail.weatherConsiderations.minTemperature}°C</span>
                                </span>
                              )}
                              {selectedTrail.weatherConsiderations.maxTemperature !== undefined && (
                                <span className="mr-4">
                                  Max temp: <span className="font-medium">{selectedTrail.weatherConsiderations.maxTemperature}°C</span>
                                </span>
                              )}
                            </div>
                          </div>
                        )}

                        {/* POIs along this trail */}
                        {selectedTrail.pointsOfInterest && selectedTrail.pointsOfInterest.length > 0 && (
                          <div>
                            <h4 className="font-semibold text-sm text-gray-700 mb-2">
                              {translate('trailDiscovery.pointsOfInterest', 'Points of Interest Along This Trail')}
                            </h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {selectedTrail.pointsOfInterest.map(poi => {
                                const poiDetails = pois.find(p => p.id === poi.poiId);
                                if (!poiDetails) return null;

                                return (
                                  <div
                                    key={poi.poiId}
                                    className="p-2 border border-gray-200 rounded-md flex items-center cursor-pointer hover:bg-gray-50"
                                    onClick={() => handlePOISelect(poiDetails)}
                                  >
                                    <div className="mr-2 text-lg text-blue-500">
                                      <FaMapMarkerAlt />
                                    </div>
                                    <div>
                                      <h5 className="font-medium text-sm">{poiDetails.name}</h5>
                                      <p className="text-xs text-gray-600">
                                        {poi.distanceFromStart.toFixed(1)} km from start
                                        {poi.detourDistance && poi.detourDistance > 0 &&
                                          ` (${poi.detourDistance.toFixed(1)} km detour)`
                                        }
                                      </p>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {selectedPOI && (
                      <div className={selectedTrail ? "pt-4 border-t border-gray-200" : ""}>
                        <h3 className="text-xl font-bold mb-2">{selectedPOI.name}</h3>
                        <div className="flex items-center mb-2">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 capitalize">
                            {selectedPOI.type.replace('_', ' ')}
                          </span>
                        </div>
                        <p className="text-gray-700 mb-3">{selectedPOI.description}</p>

                        {/* POI details */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-3">
                          {selectedPOI.openingHours && (
                            <div className="text-sm">
                              <span className="font-semibold block">Opening Hours:</span>
                              {selectedPOI.openingHours}
                            </div>
                          )}
                          {selectedPOI.contactInfo && (
                            <div className="text-sm">
                              <span className="font-semibold block">Contact:</span>
                              {selectedPOI.contactInfo}
                            </div>
                          )}
                          {selectedPOI.website && (
                            <div className="text-sm col-span-full">
                              <span className="font-semibold block">Website:</span>
                              <a
                                href={selectedPOI.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline"
                              >
                                {selectedPOI.website}
                              </a>
                            </div>
                          )}
                        </div>

                        {/* Connected trails */}
                        {!selectedTrail && (
                          <div>
                            <h4 className="font-semibold text-sm text-gray-700 mb-2">
                              {translate('trailDiscovery.connectedTrails', 'Trails Near This Point')}
                            </h4>
                            <div className="grid grid-cols-1 gap-2">
                              {(trails || [])
                                .filter(trail => trail.pointsOfInterest?.some(p => p.poiId === selectedPOI.id))
                                .map(trail => (
                                  <div
                                    key={trail.id}
                                    className="p-2 border border-gray-200 rounded-md flex items-center cursor-pointer hover:bg-gray-50"
                                    onClick={() => handleTrailSelect(trail)}
                                  >
                                    <div className="mr-2 text-lg text-green-500">
                                      <FaRoute />
                                    </div>
                                    <div>
                                      <h5 className="font-medium text-sm">{trail.name}</h5>
                                      <p className="text-xs text-gray-600">
                                        {trail.difficulty} • {trail.totalDistance} km
                                      </p>
                                    </div>
                                  </div>
                                ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Navigation mode options */}
                    <div ref={navigationButtonsRef} className="mt-4 flex flex-wrap gap-3">
                      {selectedTrail && (
                        <div className="w-full">
                          <div className="flex flex-col md:flex-row gap-3 mb-3">
                            <Link
                              href={`/${lang}/trail-discovery/navigation?trailId=${selectedTrail.id}&showTutorial=true`}
                              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center"
                            >
                              <FaQuestionCircle className="mr-2" />
                              {translate('trailDiscovery.beginnerMode', 'First time? Use help')}
                            </Link>
                            <Link
                              href={`/${lang}/trail-discovery/navigation?trailId=${selectedTrail.id}`}
                              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center justify-center"
                            >
                              <FaRoute className="mr-2" />
                              {translate('trailDiscovery.proMode', 'Professional mode')}
                            </Link>
                          </div>
                          <p className="text-xs text-gray-500 mb-3">
                            {translate('trailDiscovery.modeDescription', 'Professional mode is optimized for navigation with a clean, transparent interface. Choose help mode if you need assistance.')}
                          </p>
                        </div>
                      )}
                      <button
                        className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                        onClick={() => {
                          setSelectedTrail(null);
                          setSelectedPOI(null);
                        }}
                      >
                        {translate('trailDiscovery.close', 'Close Details')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Seasonal Highlights Section */}
        <div className="my-12 relative overflow-hidden rounded-xl">
          <div className="absolute inset-0 bg-cover bg-center opacity-20"
            style={{ backgroundImage: `url(/images/attractions/${currentSeason === 'winter' ? 'skiing-in-engelberg-photos' : 'engelberg-landscapes'}/cover.webp)` }} />

          <div className="relative z-10 p-6">
            <h2 className="text-2xl font-bold mb-2">
              {translate(`trailDiscovery.season.${currentSeason}`, getSeasonTitle(currentSeason))}
            </h2>
            <p className="text-lg mb-6">
              {translate(`trailDiscovery.seasonDesc.${currentSeason}`, getSeasonDescription(currentSeason))}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {seasonalTrails.slice(0, 4).map(trail => (
                <div
                  key={trail.id}
                  className="bg-white bg-opacity-90 rounded-lg shadow-md overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => handleTrailSelect(trail, true)}
                >
                  <div className="p-4">
                    <h3 className="font-bold text-lg mb-1">{trail.name}</h3>
                    <div className="flex items-center mb-2">
                      <span className="text-sm text-gray-500 mr-3">{trail.totalDistance} km</span>
                      <span className="text-sm text-gray-500 mr-3">{trail.estimatedDuration} hrs</span>
                      <span className="px-2 py-0.5 bg-gray-100 rounded text-xs capitalize">{trail.difficulty}</span>
                    </div>

                    {trail.seasonalNotes && trail.seasonalNotes[currentSeason] && (
                      <div className="text-sm bg-blue-50 p-2 rounded border-l-2 border-blue-300 mb-3">
                        <div className="flex items-center mb-1">
                          <span className="text-xs font-medium text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full">
                            {currentSeason.charAt(0).toUpperCase() + currentSeason.slice(1)} Note
                          </span>
                        </div>
                        <p className="italic text-gray-700">{trail.seasonalNotes[currentSeason]}</p>
                      </div>
                    )}

                    <div className="flex justify-between items-center">
                      <div className="flex space-x-1">
                        {trail.tags?.slice(0, 3).map(tag => (
                          <span key={tag} className="inline-block px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded">
                            {tag}
                          </span>
                        ))}
                      </div>
                      <button className="text-blue-600 text-sm font-medium hover:underline">
                        {translate('trailDiscovery.viewDetails', 'View Details')}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 text-center">
              <div className="inline-flex rounded-md">
                {(['spring', 'summer', 'autumn', 'winter'] as Season[]).map((season) => (
                  <button
                    key={season}
                    onClick={() => setCurrentSeason(season)}
                    className={`px-4 py-2 text-sm font-medium ${currentSeason === season
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                      } ${season === 'spring' ? 'rounded-l-md' : season === 'winter' ? 'rounded-r-md' : ''
                      }`}
                  >
                    {translate(`trailDiscovery.${season}`, season)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Tutorial Overlay */}
        <TutorialOverlay
          isOpen={showTutorial}
          onClose={() => setShowTutorial(false)}
          lang={lang}
        />

        {/* Search Modal */}
        <TrailSearchModal
          isOpen={showSearchModal}
          onClose={() => setShowSearchModal(false)}
          trails={trails}
          onTrailSelect={(trail) => {
            handleTrailSelect(trail, true);
            setShowSearchModal(false);
          }}
          currentSeason={currentSeason}
        />

        {/* Link to Trail Mapper */}
        <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-6 shadow-sm mb-8">
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-2/3 mb-4 md:mb-0 md:pr-6">
              <h3 className="text-xl font-bold mb-2">
                {translate('trailDiscovery.createYourOwn', 'Create Your Own Trail')}
              </h3>
              <p className="text-gray-700 mb-4">
                {translate('trailDiscovery.createDesc', 'Want to create and record your own custom trail? Use our Trail Mapper tool to design, record, and save your personal hiking routes in Engelberg.')}
              </p>
              <Link
                href="/trail-mapper"
                className="inline-flex items-center px-4 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors"
              >
                <FaRoute className="mr-2" />
                {translate('trailDiscovery.openTrailMapper', 'Open Trail Mapper')}
              </Link>
            </div>
            <div className="md:w-1/3">
              <div className="bg-white p-4 rounded-lg shadow-md">
                <Image
                  src="/images/attractions/trips/cover.webp"
                  alt="Trail Mapper Preview"
                  width={300}
                  height={200}
                  className="rounded"
                  style={{ height: "auto" }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Global styles for animations */}
      <style jsx global>{`
        @keyframes loading {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(200%);
          }
        }
        
        .trail-hover-effect {
          transition: all 0.3s ease;
        }
        
        .trail-hover-effect:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
        }
      `}</style>
    </main>
  );
}
